import { isReaddyCdnUrl } from "@/utils/media";

/**
 * Runtime vendor asset guard.
 *
 * The icon fonts (Remix Icon, Font Awesome), the web fonts and the browser
 * favicon are declared in index.html from OUR OWN domain under /uploads — no
 * public CDN is in the normal critical path. `scripts/sync-vendor-assets.mjs`
 * mirrors them into:
 *   /uploads/vendor/<name>   and   /uploads/brand/favicon.png
 *
 * This module does NOT swap CDN links any more (there are none to swap). It is a
 * safety net: if a mirrored file is ever missing on the server, it pulls the
 * matching public CDN stylesheet / icon back in as a last resort, so the page
 * never loses its icons or fonts. When the local files exist, the CDN is never
 * requested at all.
 */

const UPLOADS = "/uploads";
const LOCAL_FAVICON = `${UPLOADS}/brand/favicon.png`;

/** Each self-hosted stylesheet plus the public CDN to fall back to if it 404s. */
const STYLESHEET_FALLBACKS: ReadonlyArray<{ local: string; cdn: string }> = [
  {
    local: `${UPLOADS}/vendor/remixicon/remixicon.min.css`,
    cdn: "https://cdnjs.cloudflare.com/ajax/libs/remixicon/4.5.0/remixicon.min.css",
  },
  {
    local: `${UPLOADS}/vendor/font-awesome/all.min.css`,
    cdn: "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css",
  },
  {
    local: `${UPLOADS}/vendor/google-fonts/plus-jakarta-sans.css`,
    cdn: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap",
  },
];

/** Best-effort MIME type for an icon href, so the link advertises its format. */
function iconMimeType(href: string): string {
  const clean = href.split(/[?#]/)[0].toLowerCase();
  if (clean.endsWith(".png")) return "image/png";
  if (clean.endsWith(".jpg") || clean.endsWith(".jpeg")) return "image/jpeg";
  if (clean.endsWith(".webp")) return "image/webp";
  if (clean.endsWith(".gif")) return "image/gif";
  if (clean.endsWith(".ico")) return "image/x-icon";
  if (clean.endsWith(".svg")) return "image/svg+xml";
  return "";
}

/** Replace the head's icon links with a single icon + apple-touch-icon. */
function setIconLinks(href: string): void {
  if (!href) return;
  document.head
    .querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]')
    .forEach((el) => el.remove());

  const mime = iconMimeType(href);
  const isPng = mime === "image/png";

  const icon = document.createElement("link");
  icon.rel = "icon";
  if (mime) icon.type = mime;
  // Our uploaded favicons are normalised to a standard 32×32 square PNG; declare
  // that so the browser picks it as the tab icon without upscaling a stretched
  // image. Other (pasted) URLs are left size-agnostic.
  if (isPng) icon.sizes = "32x32";
  icon.href = href;
  document.head.appendChild(icon);

  const apple = document.createElement("link");
  apple.rel = "apple-touch-icon";
  if (mime) apple.type = mime;
  apple.href = href;
  document.head.appendChild(apple);
}

/**
 * Apply the tab icon, preferring the mirrored local copy under /uploads/brand.
 *
 * - When the admin has configured a NON-Readdy source (their own upload or
 *   their own CDN) it is used directly.
 * - Otherwise (remote Readdy URL, or nothing configured) the local mirror is
 *   probed first, and the configured icon is used only if the mirror is
 *   missing — so a wiped /uploads never breaks the favicon.
 */
export function applyFavicon(faviconUrl?: string): void {
  const configured = (faviconUrl || "").trim();

  if (configured && !isReaddyCdnUrl(configured)) {
    setIconLinks(configured);
    return;
  }

  const fallback = configured || LOCAL_FAVICON;
  const probe = new Image();
  probe.onload = () => setIconLinks(LOCAL_FAVICON);
  probe.onerror = () => setIconLinks(fallback);
  probe.src = LOCAL_FAVICON;
}

/** Find the <link> in the head that points at a given self-hosted stylesheet. */
function findLocalStylesheet(local: string): HTMLLinkElement | undefined {
  const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'));
  return links.find((link) => {
    const href = link.getAttribute("href") || "";
    return href === local || link.href.endsWith(local);
  });
}

/** Append the public CDN stylesheet when the local copy could not be loaded. */
function injectCdnFallback(link: HTMLLinkElement, cdn: string): void {
  if (link.dataset.cdnFallback) return;
  link.dataset.cdnFallback = "1";

  const fallback = document.createElement("link");
  fallback.rel = "stylesheet";
  fallback.href = cdn;
  fallback.dataset.vendorFallback = "1";
  document.head.appendChild(fallback);
}

/** Watch each self-hosted stylesheet and fall back to its CDN twin if it fails. */
function guardStylesheets(): void {
  for (const { local, cdn } of STYLESHEET_FALLBACKS) {
    const link = findLocalStylesheet(local);
    if (!link) continue;

    link.addEventListener("error", () => injectCdnFallback(link, cdn), { once: true });

    // A stylesheet that already failed before this script ran will not emit the
    // "error" event again, so check the resolved sheet once the page settles.
    // `sheet === null` means the local file did not load.
    const checkFailed = () => {
      if (link.sheet === null) injectCdnFallback(link, cdn);
    };
    if (document.readyState === "complete") checkFailed();
    else window.addEventListener("load", checkFailed, { once: true });
  }
}

/** Installs the runtime vendor self-hosting guard. Safe to call once. */
export function installVendorAssetHost(): void {
  if (typeof window === "undefined") return;

  const start = () => {
    guardStylesheets();
    applyFavicon();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
}